import React, {useEffect, useRef, useState} from "react";
import {
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
} from "@material-ui/core";
import {Refresh} from "@material-ui/icons";
import getZoomedFontSize from "utils/get_zoomed_font_size";

const ANIMATION_DURATION_MS = 10000;

const SVG_SIZE = 44;
const SVG_CENTER = SVG_SIZE / 2;
const CIRCLE_RADIUS = 20.2;
const STROKE_WIDTH = 3.6;
const BACKGROUND_OPACITY = 0.2;
const TEXT_FONT_SIZE = 16;
const TEXT_OPACITY = 0.8;
const PROGRESS_TRANSITION_MS = 300;
const SPINNER_SIZE = 30;
const LARGE_ICON_BASE_SIZE = 24;

interface LlmMeta {
  completed_task_count: number;
  total_task_count: number;
}

interface LlmProcessingStatusProps {
  processing: boolean;
  hasFailed: boolean;
  isAwaitingReprocessing: boolean;
  llmMeta: LlmMeta;
  color: string;
  zoom: number;
  onReprocess: () => void;
}

export default function LlmProcessingStatus({
  processing,
  hasFailed,
  isAwaitingReprocessing,
  llmMeta,
  color,
  zoom,
  onReprocess,
}: LlmProcessingStatusProps) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const largeIconSize = getZoomedFontSize(
    LARGE_ICON_BASE_SIZE,
    "checklist",
    zoom,
  );
  const issueProgress = llmMeta.completed_task_count / llmMeta.total_task_count;
  const targetPercent = Math.floor(issueProgress * 100);
  const showIssueProgress = isFinite(issueProgress) && !isNaN(issueProgress);

  const [displayedPercent, setDisplayedPercent] = useState(targetPercent);
  const displayedPercentRef = useRef(displayedPercent);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    if (!showIssueProgress) {
      return;
    }

    const from = displayedPercentRef.current;
    const to = targetPercent;

    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
    }

    if (from === to) {
      return;
    }

    const startTime = performance.now();

    function step(now: number) {
      const t = Math.min((now - startTime) / ANIMATION_DURATION_MS, 1);
      const current = Math.round(from + (to - from) * t);
      setDisplayedPercent(current);
      displayedPercentRef.current = current;
      if (t < 1) {
        animationRef.current = requestAnimationFrame(step);
      } else {
        animationRef.current = null;
      }
    }

    animationRef.current = requestAnimationFrame(step);

    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [targetPercent, showIssueProgress]);

  return (
    <div style={{display: "flex", height: "100%", alignItems: "center"}}>
      {processing ? (
        <div style={{display: "flex", alignItems: "center"}}>
          {showIssueProgress ? (
            <svg
              width={largeIconSize}
              height={largeIconSize}
              viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
              style={{padding: "0em 0.5em", flexShrink: 0, display: "block"}}
            >
              <circle
                cx={SVG_CENTER}
                cy={SVG_CENTER}
                r={CIRCLE_RADIUS}
                fill="none"
                stroke={color}
                strokeWidth={STROKE_WIDTH}
                opacity={BACKGROUND_OPACITY}
              />
              <circle
                cx={SVG_CENTER}
                cy={SVG_CENTER}
                r={CIRCLE_RADIUS}
                fill="none"
                stroke={primaryColor}
                strokeWidth={STROKE_WIDTH}
                strokeDasharray={2 * Math.PI * CIRCLE_RADIUS}
                strokeDashoffset={
                  (1 - displayedPercent / 100) * 2 * Math.PI * CIRCLE_RADIUS
                }
                transform={`rotate(-90 ${SVG_CENTER} ${SVG_CENTER})`}
                style={{
                  transition: `stroke-dashoffset ${PROGRESS_TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                }}
              />
              <text
                x={SVG_CENTER}
                y={SVG_CENTER}
                textAnchor="middle"
                dominantBaseline="central"
                fill={color}
                fontSize={TEXT_FONT_SIZE}
                opacity={TEXT_OPACITY}
              >
                {`${displayedPercent}%`}
              </text>
            </svg>
          ) : (
            <CircularProgress
              style={{
                padding: "0em 0.5em",
                width: SPINNER_SIZE,
                height: SPINNER_SIZE,
              }}
            />
          )}
        </div>
      ) : hasFailed && !isAwaitingReprocessing ? (
        <div
          style={{display: "flex", alignItems: "center", padding: "0 0.5em"}}
        >
          <Tooltip title="Reprocess Issue">
            <IconButton
              onClick={event => {
                event.stopPropagation();
                onReprocess();
              }}
              style={{padding: "0"}}
            >
              <Refresh
                style={{color, width: largeIconSize, height: largeIconSize}}
              />
            </IconButton>
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
}
